<script setup lang="ts">
import type { HTMLAttributes } from 'vue'
import type { NumberFieldRootEmits, NumberFieldRootProps } from 'reka-ui'
import { reactiveOmit } from '@vueuse/core'
import { NumberFieldRoot, useForwardPropsEmits } from 'reka-ui'
import { cn } from '@ds/lib/utils'

/*
 * Reka's NumberField. What it buys over `<input type="number">`: locale-aware
 * parsing and formatting (Intl.NumberFormat via `formatOptions`, locale from
 * ConfigProvider), min/max clamping with step snapping, arrow-key and
 * page-up/down stepping, and `disableWheelChange` for the classic bug where a
 * scroll over a focused field silently rewrites the number.
 *
 * `modelValue` is a real number, not a string — the input's *text* may be
 * grouped ("1,250,000") while the model stays 1250000.
 */
const props = defineProps<NumberFieldRootProps & { class?: HTMLAttributes['class'] }>()
const emits = defineEmits<NumberFieldRootEmits>()

const delegatedProps = reactiveOmit(props, 'class')

const forwarded = useForwardPropsEmits(delegatedProps, emits)
</script>

<template>
  <NumberFieldRoot
    v-bind="forwarded"
    :class="cn('grid gap-1.5', props.class)"
  >
    <slot />
  </NumberFieldRoot>
</template>
